import { usePreviewPrintVersionQuery } from "../hooks/usePreviewPrintVersionQuery";
import { VersionChangeIndicator } from "./VersionChangeIndicator";
import { NextVersion } from "../types/version_management_types";
import { shortSoftwareVersion } from "../utils/version_management_utils";
import { useContext } from "react";
import { ApplicationContext } from "../../../components/ApplicationProvider";

export interface GroupVersionChangeIndicatorProps {
    currentVersion?: string;
    currentTagId?: number;
    /**
     * Next Version can be undefined, null or NextVersion.
     * undefined - Not set by the user yet
     * null - Means the group will inherit the version from the company.
     * NextVersion - Tag chosen by the user
     */
    nextVersion?: null | NextVersion;
}

/**
 * GroupVersionChangeIndicator
 *
 * Shows the current version and next version under the following conditions.
 *
 * If the next version is undefined, it will not show anything.
 * If the next version is null, it will show "Default".
 * If the next version is different from the current version, it will show the next version.
 */
export function GroupVersionChangeIndicator({
    currentVersion,
    currentTagId,
    nextVersion,
}: GroupVersionChangeIndicatorProps) {
    const { t } = useContext(ApplicationContext);
    const SAME_AS_COMPANY_TAG_NAME = t("version_management.groups.same_as_company_tag_name");

    const { loading, error, data } = usePreviewPrintVersionQuery(
        nextVersion?.updateStrategy,
        nextVersion?.tagId
    );

    let nextVersionText;
    // Only show the next version if it's been changed and it's not the same as the current version
    if (nextVersion !== undefined && currentTagId !== nextVersion?.tagId) {
        nextVersionText =
            nextVersion === null
                ? SAME_AS_COMPANY_TAG_NAME
                : shortSoftwareVersion(data?.getPreviewPrintVersion?.productVersion);
    }

    const currenVersionText = currentVersion
        ? shortSoftwareVersion(currentVersion)
        : SAME_AS_COMPANY_TAG_NAME;

    return (
        <VersionChangeIndicator
            currentVersion={currenVersionText}
            isLoadingNextVersion={loading}
            errorNextVersion={error?.message}
            nextVersion={nextVersionText}
        />
    );
}
